import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { apiRequest } from "@/lib/api";
import {
  buildForm,
  countQueued,
  isOfflineError,
  listQueued,
  markAttempt,
  removeQueued,
  type QueuedItem,
} from "@/lib/offline-queue";

/** Reenvia UM registo pendente. Lança se falhar. */
const send = async (item: QueuedItem): Promise<void> => {
  switch (item.kind) {
    case "gate-entry":
      // JSON (o endpoint continua a receber JSON — os anexos vão à parte).
      await apiRequest("/security/gate-entries", { method: "POST", body: item.payload });
      return;

    case "incident":
      // Multipart: pode levar N anexos (fotos, PDF).
      await apiRequest("/security/incidents", { method: "POST", body: buildForm(item.payload) });
      return;

    case "visit-check-in":
    case "visit-check-out": {
      // Check-in/out feito na portaria sem rede. O endpoint é IDEMPOTENTE: se
      // este reenvio for um duplicado, o servidor devolve o estado actual sem
      // criar uma segunda entrada nem avisar o anfitrião outra vez.
      const { visit_id: visitId, ...body } = item.payload as { visit_id: string };
      const acao = item.kind === "visit-check-in" ? "check-in" : "check-out";
      await apiRequest(`/security/visits/${visitId}/${acao}`, { method: "POST", body });
      return;
    }
  }
};

/**
 * Sincroniza os registos que ficaram por enviar (ver lib/offline-queue).
 *
 * Corre quando a rede volta (`online`), quando a app volta a ficar visível
 * (o guarda tirou-a do bolso) e uma vez ao montar. Não faz polling: sem rede,
 * tentar de X em X segundos só gasta bateria.
 */
export const useOfflineSync = () => {
  const qc = useQueryClient();
  const [pending, setPending] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));

  const refreshCount = useCallback(async () => {
    try {
      setPending(await countQueued());
    } catch {
      /* IndexedDB indisponível (ex.: modo privado) — a app funciona na mesma, só sem fila. */
    }
  }, []);

  const sync = useCallback(async () => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return;

    let items: QueuedItem[];
    try {
      items = await listQueued();
    } catch {
      return;
    }
    if (items.length === 0) return;

    setIsSyncing(true);
    let sent = 0;

    for (const item of items) {
      try {
        await send(item);
        await removeQueued(item.id);
        sent += 1;
      } catch (error) {
        if (isOfflineError(error)) {
          // Ainda sem rede: pára já. Insistir nos restantes só ia falhar igual.
          await markAttempt(item);
          break;
        }
        // O servidor recusou (dados inválidos, sessão expirada...). Reenviar
        // isto para sempre nunca vai funcionar e entupia a fila — fora daqui,
        // mas avisando o segurança para ele poder registar de novo.
        await removeQueued(item.id);
        toast.error(`Não foi possível sincronizar: ${item.label}`, {
          description: error instanceof Error ? error.message : "Registo rejeitado pelo servidor",
          duration: 10000,
        });
      }
    }

    setIsSyncing(false);
    await refreshCount();

    if (sent > 0) {
      toast.success(sent === 1 ? "1 registo sincronizado" : `${sent} registos sincronizados`);
      void qc.invalidateQueries({ queryKey: ["security"] });
    }
  }, [qc, refreshCount]);

  useEffect(() => {
    void refreshCount();
    void sync();

    const onOnline = () => {
      setIsOnline(true);
      void sync();
    };
    const onOffline = () => setIsOnline(false);
    const onVisible = () => {
      if (document.visibilityState === "visible") void sync();
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshCount, sync]);

  return { pending, isSyncing, isOnline, sync, refreshCount };
};
